import { makeAutoObservable, runInAction } from 'mobx'
import produce from 'immer'
import { AxiosProgressEvent } from 'axios'
import { resizeImage } from '../utils/image'
import { API } from '../services/api'
import { ApiResponse } from 'apisauce'
import AlbumModel from '../models/album'
import { WorkspacesStore } from './WorkspaceStore'

export const ADD_PHOTOS_STORE = 'addPhotosStore'

class MiniaturesManager {
  private _queue: Array<FileData> = []
  private _generator: AsyncGenerator<string, void, string>

  private static _instance: MiniaturesManager
  public static get instance(): MiniaturesManager {
    return this._instance ?? (this._instance = new MiniaturesManager())
  }

  private _isRunning: boolean = false
  public get isRunning(): boolean {
    return this._isRunning
  }

  private constructor() {
    this._generator = this._generate()
  }

  private _start = async (): Promise<void> => {
    if (this._isRunning) throw new Error('Cannot start while running.')
    this._isRunning = true

    let hasValue: boolean = false
    let value: string
    do {
      const result = await this._generator.next(value)

      if (result.value) {
        hasValue = true
        value = result.value
      } else {
        hasValue = false
      }
    } while (hasValue)
    this._isRunning = false
  }

  public generateMiniature = (fileData: FileData) => {
    const alreadyScheduled = this._queue.indexOf(fileData) >= 0
    /// if file is already in queue do not schedule it again
    if (alreadyScheduled) return
    this._queue.push(fileData)
    if (!this.isRunning) {
      this._start()
    }
  }

  /// clears queue
  public clear = () => {
    this._queue = []
  }

  /// generate miniatures
  private async *_generate(): AsyncGenerator<string, never, string> {
    do {
      const fileData = this._queue.find((data) => !data.miniatureLoaded)
      if (fileData) {
        try {
          const url = yield resizeImage(fileData.file)
          fileData.setMiniatureUrl(url)
        } catch (err) {
          console.error('cannot create thumbnail url', err)
          yield null
          fileData.setMiniatureUrl(null)
        }
      } else {
        yield undefined
      }
    } while (true)
  }
}

class Upload {
  private _progress: number = 0
  private _uploaded: number = 0
  private _total: number = 0

  constructor() {
    makeAutoObservable(this)
  }

  get progress(): number {
    return this._progress ?? 0
  }

  get uploaded(): number {
    return this._uploaded ?? 0
  }

  get total(): number {
    return this._total ?? 0
  }

  get done(): boolean {
    return this.progress === 100
  }

  handler = (value: AxiosProgressEvent) => {
    this._progress = ((value.loaded ?? 0) / (value.total ?? 1)) * 100
    this._total = value.total ?? 0
    this._uploaded = value.loaded ?? 0
  }
}

export class FileData {
  album: AlbumModel | undefined = undefined
  workspaceId: string = ''
  isPublic: boolean | undefined = undefined
  upload: Upload | undefined = undefined
  private _miniatureUrl: string | undefined = undefined
  sending: boolean = false
  response: ApiResponse<any, any> | undefined = undefined

  constructor(public readonly key: string, public readonly file: File) {
    makeAutoObservable(this)
    this._miniatureUrl = undefined
    this.isPublic = false
    this.album = undefined
  }

  get miniatureUrl(): string | undefined {
    if (!this._miniatureUrl) {
      MiniaturesManager.instance.generateMiniature(this)
    }
    return this._miniatureUrl
  }

  get miniatureLoaded(): boolean {
    return this._miniatureUrl !== undefined
  }

  get uploaded(): boolean {
    return (this.upload?.done && Boolean(this.response)) ?? false
  }

  get hasError(): boolean {
    return this.response?.ok === false
  }

  get errorCode(): number | undefined {
    return this.response?.status
  }

  get isDuplicate(): boolean {
    return this.errorCode === 409
  }

  get errorMessage(): string | undefined {
    if (this.isDuplicate) {
      return 'Zdjęcie już zostało przesłane'
    }
    return this.response?.originalError?.message ?? 'Błąd przesyłania'
  }

  setIsPublic = (isPublic: boolean) => {
    this.isPublic = isPublic
  }

  setAlbum = (album: AlbumModel) => {
    this.album = album
  }

  setWorkspaceId = (workspaceId: string) => {
    this.workspaceId = workspaceId
  }

  startUpload = (): Upload => (this.upload = new Upload())
  clearUpload = (): Upload | undefined => (this.upload = undefined)
  setMiniatureUrl = (url: string | null): void => {
    this._miniatureUrl = url
  }
}

export class AddPhotosStore {
  currentUploadingFile: FileData | undefined = undefined
  currentUploadingFiles: Set<FileData> = new Set()
  files: FileData[] = []
  private uploadTask: (Promise<void> & { cancel?: () => void }) | undefined = undefined
  waitForCancel = false
  
  private static readonly MAX_CONCURRENT_UPLOADS = 4

  constructor(public readonly workspacesStore: WorkspacesStore) {
    makeAutoObservable(this)
  }

  get uploadedCount() {
    return this.files.filter((file) => file.uploaded).length
  }

  get filesCount() {
    return this.files.length
  }

  get allFilesUploaded(): boolean {
    return this.filesCount === this.uploadedCount
  }

  get isUploading(): boolean {
    return Boolean(this.uploadTask)
  }

  removeFile = (fileData: FileData) => {
    if (this.uploadTask) throw new Error('Cannot modify files list during upload')
    this.files = this.files.filter((file) => file.key !== fileData.key)
  }

  updateFile = (fileData: FileData) => {
    if (this.uploadTask) throw new Error('Cannot modify files list during upload')
    const index = this.files.findIndex((file) => file.key !== fileData.key)
    if (index < 0) throw new Error('File does not exist')
    this.files[index] = fileData
    this.files = [...this.files]
  }

  // Snapshot the active workspace onto new files so queued items don't follow later switches.
  private captureActiveWorkspace = (files: Array<FileData>): void => {
    const { activeWorkspaceId } = this.workspacesStore
    if (!activeWorkspaceId) return
    files.forEach((file) => {
      if (!file.workspaceId) file.setWorkspaceId(activeWorkspaceId)
    })
  }

  setFiles = async (files: Array<FileData>): Promise<void> => {
    if (this.uploadTask) throw new Error('Cannot select files during upload')
    this.removeFiles()
    this.captureActiveWorkspace(files)
    this.files = files
  }

  addFiles = async (files: Array<FileData>): Promise<void> => {
    this.captureActiveWorkspace(files)
    this.files = [...this.files, ...files]

    /// If us not uploading, start upload
    if (!this.uploadTask) {
      this.startUpload()
    }
  }

  /** Append files to queue without starting upload */
  queueFiles = (files: Array<FileData>): void => {
    this.captureActiveWorkspace(files)
    this.files = [...this.files, ...files]
  }

  stopUpload = () => {
    this.waitForCancel = true
  }

  startUpload = (): void => {
    if (this.waitForCancel) {
      this.waitForCancel = false
      return
    }
    const task = this._upload()

    /// remove upload task on done
    const cleanup = () =>
      runInAction(() => {
        this.uploadTask = undefined
        this.waitForCancel = false
      })
    task.then(cleanup).catch(cleanup)

    this.uploadTask = task
  }

  private _upload = async (): Promise<void> => {
    const pendingFiles = this.files.filter(f => !f.uploaded && !f.sending)
    
    // Process files in batches of MAX_CONCURRENT_UPLOADS
    const uploadFile = async (fileData: FileData, index: number): Promise<void> => {
      if (this.waitForCancel) return
      
      runInAction(() => {
        this.currentUploadingFiles.add(fileData)
        fileData.sending = true
      })

      const response: ApiResponse<any, any> = await API.getInstance().uploadPhoto(
        {
          workspaceId: fileData.workspaceId,
          photo: fileData.file,
          albumId: fileData.album?.id,
          isPublic: fileData.isPublic,
        },
        fileData.startUpload().handler
      )

      runInAction(() => {
        fileData.response = response
        fileData.sending = false
        this.currentUploadingFiles.delete(fileData)
      })
    }

    // Create a queue-based concurrent upload system
    let currentIndex = 0
    const getNextFile = (): { fileData: FileData; index: number } | null => {
      while (currentIndex < this.files.length) {
        const fileData = this.files[currentIndex]
        const idx = currentIndex
        currentIndex++
        if (!fileData.uploaded && !fileData.response) {
          return { fileData, index: idx }
        }
      }
      return null
    }

    const worker = async (): Promise<void> => {
      while (!this.waitForCancel) {
        const next = getNextFile()
        if (!next) break
        await uploadFile(next.fileData, next.index)
      }
    }

    // Start MAX_CONCURRENT_UPLOADS workers
    const workers = Array(AddPhotosStore.MAX_CONCURRENT_UPLOADS)
      .fill(null)
      .map(() => worker())

    await Promise.all(workers)

    if (this.waitForCancel) {
      throw new Error('CANCELATION')
    }

    runInAction(() => {
      this.currentUploadingFile = undefined
      this.currentUploadingFiles.clear()
    })
  }

  removeSentFiles = () => {
    if (this.uploadTask) throw new Error('Cannot modify files list during upload')
    this.files = this.files.filter((file) => !file.uploaded)
  }

  removeFiles = () => {
    if (this.uploadTask) throw new Error('Cannot modify files list during upload')
    MiniaturesManager.instance.clear()
    this.files = []
  }
}
